import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Inbox, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const Flag = ({ ok, labelOk, labelBad }) => (
  ok ? (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> {labelOk}</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-red-400"><XCircle className="w-3.5 h-3.5" /> {labelBad}</span>
  )
);

const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export const ConnectedMailboxesCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.getMailHealth()
      .then(res => setData(res.data.mailboxes || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (!data) return null;

  return (
    <div className="border border-border rounded-sm bg-card p-4" data-testid="mailboxes-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
          <Inbox className="w-4 h-4 text-sky-400" /> Connected Mailboxes
        </h3>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="mailboxes-refresh-btn"
          className="border-border text-muted-foreground rounded-sm text-[11px] h-7 px-2.5">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Check
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Every Microsoft account connected to the platform — inboxes with "Mail read" are scanned for
        deal correspondence and owner responses every 10 minutes.
      </p>
      {data.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid="mailboxes-empty">
          No Microsoft accounts connected in this environment.
        </p>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-secondary/50 uppercase tracking-wider text-muted-foreground font-bold text-[10px]">
                <th className="text-left py-2 px-3">Mailbox</th>
                <th className="text-left py-2 px-3">App User</th>
                <th className="text-left py-2 px-3">Mail read</th>
                <th className="text-left py-2 px-3">Mail send</th>
                <th className="text-left py-2 px-3">Calendar</th>
                <th className="text-right py-2 px-3">Last inbox scan</th>
                <th className="text-right py-2 px-3">Stored emails</th>
              </tr>
            </thead>
            <tbody>
              {data.map((m, i) => (
                <tr key={m.user_id} data-testid={`mailbox-row-${i}`} className="border-t border-border">
                  <td className="py-2 px-3 text-foreground font-mono whitespace-nowrap">{m.account || '—'}</td>
                  <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                    {m.user_name} <span className="text-[10px] uppercase text-foreground/40">{m.user_role}</span>
                  </td>
                  <td className="py-2 px-3" data-testid={`mailbox-mailread-${i}`}>
                    <Flag ok={m.mail_read_ready} labelOk="OK" labelBad="reconnect needed" />
                  </td>
                  <td className="py-2 px-3"><Flag ok={m.mail_send_ready} labelOk="OK" labelBad="—" /></td>
                  <td className="py-2 px-3"><Flag ok={m.calendar_ready} labelOk="OK" labelBad="—" /></td>
                  <td className="py-2 px-3 text-right text-muted-foreground font-mono whitespace-nowrap">{fmtTs(m.last_inbox_scan)}</td>
                  <td className="py-2 px-3 text-right text-foreground font-mono">{m.stored_emails}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.some(m => !m.mail_read_ready) && (
        <p className="text-[11px] text-amber-400 mt-2" data-testid="mailboxes-reconnect-hint">
          "Reconnect needed": that user must open Settings → Microsoft and reconnect once to grant email access (Mail.Read).
        </p>
      )}
    </div>
  );
};
